import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { parentOf } from "@egalregal/shared";
import { TopBar } from "./TopBar";
import { ContentHeader } from "./ContentHeader";
import { PhoneFooter } from "./PhoneFooter";
import { NavRail } from "./NavRail";
import { NavSidebar } from "./NavSidebar";
import { TailSheet } from "./TailSheet";
import { OfflineBanner } from "./OfflineBanner";
import { DemoBanner } from "../demo/DemoBanner";
import { resolveBackTarget, readHistoryIndex, type BackTarget } from "./back";
import { DiscardGuardProvider, useDiscardGuard } from "./discardGuard";
import { resolveContentHeader } from "./contentHeader";
import { CONTENT_MAX_WIDTH } from "./layout";
import { ScrollAreaContext } from "./scrollArea";
import { resolveRoute } from "./routes";
import { showsAppChrome, useShell } from "./useShell";
import { applySearchState, readSearchState, searchUrl } from "../search/searchUrl";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";

/**
 * The app frame (spec §2–3): one flex column — app bar, optional offline
 * banner, then a row of nav plus the content column that owns the vertical
 * scroll. The phone footer closes the column. Nothing is sticky and nothing
 * pads for the bar, so the old sticky + inner-padding dead strip cannot exist.
 *
 * Every piece of chrome is derived from the route: the back target and label
 * from the back contract, the selected nav row from the destination, the
 * content header from the route predicate. No store navigation state decides
 * what renders. The frame also owns the discard guard, so every chrome exit
 * asks before an edit form loses its draft (spec §3).
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <DiscardGuardProvider>
      <ShellFrame>{children}</ShellFrame>
    </DiscardGuardProvider>
  );
}

function ShellFrame({ children }: { children: React.ReactNode }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const shell = useShell();
  const guard = useDiscardGuard();
  const setOnline = useAppStore((s) => s.setOnline);
  const online = useAppStore((s) => s.online);
  const auth = useAppStore((s) => s.auth);
  const containers = useAppStore((s) => s.containers);
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const [tailOpen, setTailOpen] = useState(false);
  const frameHeight = useFrameHeight(shell === "phone");
  const mainRef = useRef<HTMLDivElement | null>(null);

  const chrome = showsAppChrome(auth);
  const route = resolveRoute(location.pathname);
  // The scan surface pins its CTA to the scrollport (scan-followups 02), so its
  // content column has to be at least one scrollport tall. Only that route gets
  // the full-height flex column; every other view keeps the block flow.
  const fullHeightContent = route.fullHeight === true;
  const onSearchRoute = route.surface === "search";
  const searchState = readSearchState(
    new URLSearchParams(onSearchRoute ? location.search : ""),
  );
  const backTarget = resolveBackTarget({
    location,
    historyIndex: readHistoryIndex(),
    parentOf: (id) => parentOf(containers, id)?.id ?? null,
  });
  const header = chrome
    ? resolveContentHeader({
        pathname: location.pathname,
        route,
        backTarget,
        containers,
        entries,
        sharedWithMe,
        selectedEntryId,
        t,
      })
    : null;

  useEffect(() => {
    const online = () => setOnline(true);
    const offline = () => setOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, [setOnline]);

  // Every route change announces the new view through its one `h1` (spec §10).
  useEffect(() => {
    moveFocusToContent(mainRef.current, document.activeElement);
  }, [location.pathname]);

  /** Follow the back contract: a concrete target, or the browser history. */
  const back = (target: BackTarget) => {
    guard.request(() => {
      if (target.kind === "history") navigate(-1);
      else navigate(target.to);
    });
  };

  /** A chrome destination: the guard gets the chance to block the exit (§3). */
  const goTo = (path: string) => guard.request(() => navigate(path));

  /** The app-bar field is empty outside `/search` and mirrors `q` on it. */
  const searchFor = (value: string) => {
    const params = applySearchState(
      new URLSearchParams(onSearchRoute ? location.search : ""),
      { ...searchState, q: value },
    );
    if (onSearchRoute) {
      navigate(searchUrl(params), { replace: true });
    } else {
      // The first keystroke leaves the current view for the search surface.
      guard.request(() => navigate(searchUrl(params)));
    }
  };

  /** Focusing the field on any other view opens the search surface (spec §5). */
  const launchSearch = () => {
    if (!onSearchRoute) goTo("/search");
  };

  /** A crumb is an ancestor container or the storage root. */
  const openCrumb = (containerId: string | null) =>
    goTo(containerId ? `/browse/${containerId}` : "/browse");

  return (
    <Box
      sx={{
        height: frameHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      {chrome && (
        <TopBar
          shell={shell}
          searchValue={onSearchRoute ? searchState.q : ""}
          onSearchChange={searchFor}
          onSearchFocus={launchSearch}
          onMenu={() => setTailOpen(true)}
          onScan={() => goTo("/scan")}
        />
      )}

      {chrome && !online && <OfflineBanner />}
      {chrome && <DemoBanner />}

      <Box sx={{ flexGrow: 1, minHeight: 0, display: "flex" }}>
        {chrome && shell === "tablet" && (
          <NavRail destination={route.destination} onNavigate={goTo} />
        )}
        {chrome && shell === "desktop" && (
          <NavSidebar destination={route.destination} onNavigate={goTo} />
        )}

        <Box
          component="main"
          ref={mainRef}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            ...(fullHeightContent && { display: "flex", flexDirection: "column" }),
          }}
        >
          <ScrollAreaContext.Provider value={mainRef}>
            <Box
              sx={{
                maxWidth: CONTENT_MAX_WIDTH,
                mx: "auto",
                px: { xs: 2, md: 3, lg: 4 },
                pt: 2,
                // No bottom padding on the scan surface: its CTA fills the last
                // row and sits flush above the phone footer, not on a strip of
                // page background.
                pb: fullHeightContent ? 0 : 2,
                ...(fullHeightContent && {
                  // Fill the scrollport and stack the view over its CTA, so the
                  // bar's sticky containing block reaches past the fold.
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                }),
              }}
            >
              {chrome && (
                <ContentHeader
                  model={header}
                  compact={shell === "phone"}
                  onBack={back}
                  onCrumb={openCrumb}
                />
              )}
              {children}
            </Box>
          </ScrollAreaContext.Provider>
        </Box>
      </Box>

      {chrome && shell === "phone" && (
        <PhoneFooter destination={route.destination} onNavigate={goTo} />
      )}

      {chrome && shell === "phone" && (
        <TailSheet
          open={tailOpen}
          onClose={() => setTailOpen(false)}
          onNavigate={(path) => {
            setTailOpen(false);
            goTo(path);
          }}
        />
      )}
    </Box>
  );
}

/**
 * Announce the view that just mounted: focus its single `h1` so assistive tech
 * reads the new page title instead of leaving focus on a control that may no
 * longer exist (spec §10).
 *
 * The one exception is the app-bar search field while it launches `/search`:
 * the field is the query, so focus must stay there for typing (spec §5).
 *
 * @param main the content landmark that owns the view.
 * @param active the element that held focus when the route changed.
 */
function moveFocusToContent(main: HTMLElement | null, active: Element | null): void {
  if (!main) return;
  if (active instanceof HTMLElement && active.dataset.searchField === "true") return;
  const heading = main.querySelector("h1");
  if (!heading) return;
  // Focusable programmatically without joining the tab order.
  if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
  heading.focus();
}

/**
 * The frame height: `100dvh` plus the visualViewport lift so the phone footer
 * stays above an open keyboard (spec §2 geometry). The lifted height only
 * applies to the phone shell; tablet/desktop keep the dynamic viewport unit.
 *
 * @param phone whether the phone shell is active.
 * @returns a CSS height for the frame.
 */
function useFrameHeight(phone: boolean): string {
  const [visualHeight, setVisualHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!phone || typeof window === "undefined" || !window.visualViewport) {
      setVisualHeight(null);
      return;
    }
    const viewport = window.visualViewport;
    const update = () => setVisualHeight(viewport.height);
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [phone]);

  return phone && visualHeight ? `${visualHeight}px` : "100dvh";
}
